"use client";

import type { ChangeEvent } from "react";
import type { TranscriptionResponse } from "@ui/components/demos/STT/types";

import { useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { clsx } from "clsx";
import { AnimatePresence } from "motion/react";
import * as m from "motion/react-m";

import { Button } from "@ui/components/core/Button";
import { ListBox } from "@ui/components/core/ListBox";
import IconArrowInbox from "@ui/central-icons/icons/IconArrowInbox";
import IconPlayFill from "@ui/central-icons/icons/IconPlayFill";
import IconPauseFill from "@ui/central-icons/icons/IconPauseFill";

import { UPLOAD_VIEW_TRANSITION_MS } from "./constants";
import { useSTTFloatingCardDownloads } from "./hooks/useSTTFloatingCardDownloads";
import { STTFloatingCardUploadControls } from "./STTFloatingCardUploadControls";
import type { UploadControlsView } from "./STTFloatingCardUploadControls";

interface STTFloatingCardFooterProps {
  isPlaying: boolean;
  uploadedTranscription: TranscriptionResponse | null;
  uploadFileName: string | null;
  sampleName: string;
  allowUrlTranscription: boolean;
  onTogglePlay: () => void;
  onFileChange: (e: ChangeEvent<HTMLInputElement>) => void;
  onUrlSubmit: (url: string) => void | Promise<void>;
  onDownload?: () => void;
  onTranscriptionDownloaded?: (download_format: string) => void;
}

export function STTFloatingCardFooter({
  isPlaying,
  uploadedTranscription,
  uploadFileName,
  sampleName,
  allowUrlTranscription,
  onTogglePlay,
  onFileChange,
  onUrlSubmit,
  onDownload,
  onTranscriptionDownloaded,
}: STTFloatingCardFooterProps) {
  const t = useTranslations("ClientKeys.STTHero");
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const downloadButtonRef = useRef<HTMLButtonElement | null>(null);
  const [uploadControlsWidth, setUploadControlsWidth] = useState(0);

  const [uploadView, setUploadView] = useState<UploadControlsView>("closed");
  const [uploadLayoutView, setUploadLayoutView] = useState<UploadControlsView>("closed");
  const isUploadOverlayActive = uploadView !== "closed" || uploadLayoutView !== "closed";
  const isUrlInputLayout = uploadLayoutView === "urlInput";

  const handleUploadViewChange = (nextView: UploadControlsView) => {
    setUploadView(nextView);
    setUploadLayoutView(currentView => {
      if (nextView !== "closed") return nextView;
      return currentView === "urlInput" ? currentView : "closed";
    });
  };

  const handleUploadControlsExitComplete = () => {
    if (uploadView === "closed") {
      setUploadLayoutView("closed");
    }
  };

  // Deferred version of compact mode — lets play/download animate out at their
  // correct position before the footer collapses.
  const [isFooterCompact, setIsFooterCompact] = useState(false);
  useEffect(() => {
    if (isUploadOverlayActive) {
      const timer = setTimeout(() => setIsFooterCompact(true), UPLOAD_VIEW_TRANSITION_MS);
      return () => clearTimeout(timer);
    } else {
      setIsFooterCompact(false);
    }
  }, [isUploadOverlayActive]);

  const { formatItems, hasFormats, isFormatDropdownOpen, setIsFormatDropdownOpen, handleDownloadClick } =
    useSTTFloatingCardDownloads({
      uploadedTranscription,
      uploadFileName,
      sampleName,
      onDownload,
      onTranscriptionDownloaded,
    });

  return (
    <>
      {/* Blur overlay — always in the DOM so its compositing layer stays alive; opacity toggled */}
      <div
        className={clsx(
          "tw-absolute tw-inset-0 tw-z-20 tw-rounded-3xl tw-backdrop-blur-[3.5px] tw-bg-gradient-to-b tw-from-white/40 tw-to-white tw-transition-opacity",
          isUploadOverlayActive ? "tw-opacity-100" : "tw-opacity-0 tw-pointer-events-none",
        )}
        style={{ transitionDuration: `${UPLOAD_VIEW_TRANSITION_MS}ms` }}
      />

      {/* Footer */}
      <div
        className={clsx(
          "tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-z-30 tw-flex tw-items-end tw-pb-3 tw-px-3 tw-pointer-events-none",
          isFooterCompact ? "tw-pt-3" : "tw-bg-gradient-to-b tw-from-transparent tw-to-white tw-h-[98px] tw-pt-8",
        )}
      >
        {/* Left: play/pause */}
        <AnimatePresence initial={false}>
          {!isUploadOverlayActive && (
            <m.div
              key='play-btn'
              className='tw-flex tw-items-end'
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: UPLOAD_VIEW_TRANSITION_MS / 1000 }}
            >
              <Button
                className='tw-pointer-events-auto sm:tw-hidden'
                variant='primary'
                size='pill'
                aria-label={isPlaying ? t("pause") : t("play")}
                onClick={onTogglePlay}
              >
                {isPlaying ? <IconPauseFill className='tw-w-4 tw-h-4' /> : <IconPlayFill className='tw-w-4 tw-h-4' />}
              </Button>

              <Button
                className='tw-pointer-events-auto tw-hidden sm:tw-block'
                variant='primary'
                size='md'
                onClick={onTogglePlay}
              >
                {isPlaying ? t("pause") : t("play")}
              </Button>
            </m.div>
          )}
        </AnimatePresence>

        {/* Spacer — pushes download + upload controls to the right edge. */}
        <div className={clsx("tw-flex-1", isUrlInputLayout && "tw-hidden")} />

        <div className={clsx("tw-flex tw-items-end tw-gap-2", isUrlInputLayout && "tw-w-full")}>
          {/*
           * Download button — kept in normal flex flow so it cannot overlap the upload
           * trigger before the trigger width has been measured on initial load.
           */}
          <AnimatePresence initial={false}>
            {!isUploadOverlayActive && (
              <m.div
                key='download-btn'
                className='tw-flex tw-items-center'
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: UPLOAD_VIEW_TRANSITION_MS / 1000 }}
              >
                <Button
                  ref={downloadButtonRef}
                  className='tw-pointer-events-auto'
                  variant='elevated'
                  size='pill'
                  aria-label={t("download")}
                  aria-expanded={hasFormats ? isFormatDropdownOpen : undefined}
                  onClick={handleDownloadClick}
                >
                  <IconArrowInbox className='tw-w-4 tw-h-4' />
                </Button>

                {hasFormats && (
                  <ListBox
                    items={formatItems}
                    triggerRef={downloadButtonRef}
                    isOpen={isFormatDropdownOpen}
                    onOpenChange={setIsFormatDropdownOpen}
                    asMenu
                    placement='top end'
                    aria-label={t("download_format")}
                  />
                )}
              </m.div>
            )}
          </AnimatePresence>

          {/*
           * Upload controls — the wrapper reserves the measured closed trigger width
           * so the download button always keeps its flex gap.
           */}
          <div
            className={clsx("tw-flex tw-justify-end", isUrlInputLayout && "tw-w-full")}
            style={
              isUrlInputLayout || !uploadControlsWidth
                ? undefined
                : { width: `${uploadControlsWidth}px`, minWidth: `${uploadControlsWidth}px` }
            }
          >
            <STTFloatingCardUploadControls
              allowUrlTranscription={allowUrlTranscription}
              fileInputRef={fileInputRef}
              onClosedWidthChange={setUploadControlsWidth}
              onExitComplete={handleUploadControlsExitComplete}
              view={uploadView}
              onUrlSubmit={onUrlSubmit}
              onViewChange={handleUploadViewChange}
            />
          </div>
        </div>
      </div>

      {/* Hidden file input — lives here so the footer owns the full file-pick flow */}
      <input
        ref={fileInputRef}
        type='file'
        accept='audio/*,video/*'
        className='tw-hidden'
        onChange={onFileChange}
        aria-hidden='true'
      />
    </>
  );
}
